import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useState } from 'react';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../../actions/authActions';
import BlueDotBtn from './BlueDotBtn.jsx';
import { authSelector } from '../../../selectors/mainSelectors.js';
import { useGetUserListQuery } from '../../../api/userApi.js';
import Queue from './Queue.jsx';
import { Box, Button, IconButton, Input, Stack, Tooltip, Typography } from '@mui/material';
import { setSelectedUserId } from '../../../actions/userActions.js';
import SettingsModal from '../DashboardContent/Settings/SettingsModal.jsx';
import { isAdminSelector, isTaxExpertSelector, isTaxProSelector } from '../../../selectors/authSelectors.js';
import { IS_STAGING_TOOLS_ENABLED } from '../../../global/Environment.js';

const SideMenu = () => {
  const [currentSearchValue, setCurrentSearchValue] = useState('');
  const [submittedSearchValue, setSubmittedSearchValue] = useState('');
  const [wasSearchSubmitted, setWasSearchSubmitted] = useState(false);
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const auth = useSelector(authSelector);
  const isAdmin = useSelector(isAdminSelector);
  const isTaxExpert = useSelector(isTaxExpertSelector);
  const isTaxPro = useSelector(isTaxProSelector);

  const { user: bookkeeper } = auth;

  const { data: userList = [] } = useGetUserListQuery(submittedSearchValue);

  const handleUserClick = (userId) => {
    dispatch(setSelectedUserId(userId));
  };

  /**
   * @description Logout User
   */
  const handleLogout = () => {
    dispatch(logout());
  };

  /**
   * @description On change event
   */
  const handleChange = (e) => {
    setCurrentSearchValue(e.target.value);
  };

  const categorizeUsers = () => {
    const categorized = {
      ownExtended: [],
      otherExtended: [],
      hold: [],
      priority: [],
      normal: [],
      upcomingCalls: []
    };

    userList.forEach((user) => {
      const isOwnExtended = user.extendedAssignments?.some((assignment) => assignment.bookkeeperId === bookkeeper.id);
      const isOtherExtended = !isEmpty(user.extendedAssignments) && !isOwnExtended;
      const isOnHoldByBookkeeper = user.isOnHold && user.holdingBookkeeperId === bookkeeper.id;

      if (isTaxPro) {
        if (user.hasUpcomingMeeting) {
          categorized.upcomingCalls.push(user);
        } else if (isOwnExtended && !user.hasUpcomingMeeting) {
          categorized.ownExtended.push(user);
        } else {
          categorized.normal.push(user);
        }
      } else {
        if (isOwnExtended) {
          categorized.ownExtended.push(user);
        } else if (isOtherExtended && isAdmin) {
          categorized.otherExtended.push(user);
        } else if (isOnHoldByBookkeeper) {
          categorized.hold.push(user);
        } else if (user.priority) {
          categorized.priority.push(user);
        } else if (isTaxPro) {
          categorized.upcomingCalls.push(user);
        } else {
          categorized.normal.push(user);
        }
      }
    });

    return categorized;
  };

  /**
   * @description on search user click
   * @param {String} type
   */
  const searchUser = (e) => {
    e.preventDefault();
    setSubmittedSearchValue(currentSearchValue);
    setWasSearchSubmitted(true);
  };

  const categorizedUsers = categorizeUsers();
  const shouldSurfaceSettings = isAdmin || isTaxPro;

  return (
    <>
      <Stack
        direction='column'
        sx={{
          width: '15%',
          height: '100%',
          minWidth: '215px',
          background: 'white'
        }}
      >
        <Box
          sx={{
            boxSizing: 'border-box',
            display: 'flex',
            width: '100%',
            padding: '10px',
            alignItems: 'center'
          }}
        >
          <Stack
            direction='row'
            sx={{
              alignItems: 'center',
              gap: 1
            }}
          >
            <FontAwesomeIcon icon={['fa', 'user']} />
            <Typography
              component='span'
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                textTransform: 'capitalize'
              }}
            >
              {bookkeeper.firstName} {bookkeeper.lastName}
            </Typography>
          </Stack>
          <Stack
            sx={{
              marginLeft: 'auto',
              flexDirection: 'row',
              gap: 1
            }}
          >
            {shouldSurfaceSettings && (
              <Tooltip title='Settings'>
                <IconButton
                  aria-label='Settings'
                  sx={{
                    color: 'black',
                    background: 'transparent',
                    border: 'none',
                    fontSize: '14px'
                  }}
                  onClick={() => setSettingsModalOpen(true)}
                >
                  <FontAwesomeIcon icon={['fa', 'bars']} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title='Logout'>
              <IconButton
                sx={{
                  color: 'black',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '14px'
                }}
                onClick={handleLogout}
              >
                <FontAwesomeIcon icon={['fa', 'power-off']} />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>
        {IS_STAGING_TOOLS_ENABLED && (
          <Box sx={{ px: '10px', pb: 1 }}>
            <Button
              fullWidth
              variant='contained'
              color='warning'
              size='small'
              disableElevation
              startIcon={<ScienceOutlinedIcon fontSize='small' />}
              onClick={() => navigate('/test-accounts')}
            >
              Staging tools
            </Button>
          </Box>
        )}
        <Box sx={{ minHeight: '125px' }}>
          <form onSubmit={(e) => searchUser(e)}>
            <Box
              sx={{
                p: 1,
                background: 'white'
              }}
            >
              <Box sx={{ display: 'flex', width: '100%', padding: '0 5px', marginBottom: '12px' }}>
                <Input
                  value={currentSearchValue}
                  onChange={handleChange}
                  placeholder='Id, phone, or email'
                  autoComplete='off'
                  maxLength='100'
                  sx={{
                    border: '1px solid white',
                    padding: '0 5px',
                    width: '100%'
                  }}
                />
                <IconButton
                  sx={{
                    height: '40px',
                    width: '40px',
                    border: 'none',
                    borderRadius: '50%',
                    fontSize: '16px',
                    color: 'black',
                    ml: 2,
                    mr: 1
                  }}
                  onClick={(e) => searchUser(e)}
                >
                  <FontAwesomeIcon icon={['fa', 'search']} />
                </IconButton>
              </Box>
            </Box>
          </form>
          {!isTaxExpert && <BlueDotBtn />}
        </Box>
        <Box
          sx={{
            width: '100%',
            overflowY: 'auto',
            background: '#efefef',
            height: '100%'
          }}
        >
          {userList.length === 0 && (
            <Box
              sx={{
                my: 1,
                p: 1,
                textAlign: 'center',
                fontSize: '14px',
                color: 'rgba(0, 0, 0, 0.5)'
              }}
            >
              <Typography variant='body2'>
                No users here{' '}
                <span role='img' aria-label='hmm'>
                  🤔
                </span>
              </Typography>
            </Box>
          )}
          {!isEmpty(categorizedUsers.ownExtended) && (
            <Queue
              type='extended'
              title={`Assignments${isAdmin ? ' (yours)' : ''}`}
              users={categorizedUsers.ownExtended}
              onUserClick={handleUserClick}
            />
          )}
          {!isEmpty(categorizedUsers.otherExtended) && (isAdmin || wasSearchSubmitted) && (
            <Queue
              type='extended'
              title='Assignments (all)'
              users={categorizedUsers.otherExtended}
              onUserClick={handleUserClick}
            />
          )}
          {!isEmpty(categorizedUsers.hold) && (
            <Queue type='hold' title='Hold' users={categorizedUsers.hold} onUserClick={handleUserClick} />
          )}
          {!isEmpty(categorizedUsers.priority) && (
            <Queue type='priority' title='Priority' users={categorizedUsers.priority} onUserClick={handleUserClick} />
          )}
          {!isEmpty(categorizedUsers.normal) && (
            <Queue
              type='normal'
              title='Normal Priority'
              users={categorizedUsers.normal}
              onUserClick={handleUserClick}
            />
          )}
          {!isEmpty(categorizedUsers.upcomingCalls) && (
            <Queue
              type='upcoming-calls'
              title='Upcoming Calls'
              users={categorizedUsers.upcomingCalls}
              onUserClick={handleUserClick}
            />
          )}
        </Box>
      </Stack>
      {shouldSurfaceSettings && <SettingsModal isOpen={settingsModalOpen} setIsOpen={setSettingsModalOpen} />}
    </>
  );
};

export default SideMenu;
